// hooks/market/useMarketFeed.ts
//
// Récupère le feed paginé de produits publiés (toutes boutiques
// confondues), pour app/(tabs)/market/index.tsx. Appelle le vrai
// backend :
//   POST {API_BASE_URL}/market   body: { action: 'get-feed', access_token, limit, offset }
// (voir backend/handleMarket.ts → getFeed, basé sur la vue
// market_marketplace_feed)
//
// PATCH V12 : le champ `name` était incorrect — la vue
// market_marketplace_feed n'expose pas de colonne `name` pour le
// produit (elle est aliasée `product_name`, `name` étant déjà pris par
// `s.name AS shop_name`). Le backend sélectionnait littéralement
// `name`, ce qui aurait fait échouer get-feed / get-shop-products /
// get-favorites avec une erreur Postgrest ("column does not exist") —
// corrigé côté backend (handleMarket.ts), et corrigé ici en miroir :
// `name` → `product_name`. Ajout aussi de `learn_more_url`, présent
// dans la vue mais absent du type jusqu'ici alors que
// show_learn_more_button y figurait déjà.
//
// ⚠️ Impact : tout composant qui lisait `product.name` sur un
// MarketFeedProduct (ou un MarketProductDetail, qui en hérite via
// useProduct.ts) doit être mis à jour vers `product.product_name`.

import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

// Miroir des colonnes retournées par market_marketplace_feed (voir
// backend/handleMarket.ts → getFeed).
export type MarketFeedProduct = {
  product_id: string;
  shop_id: string;
  owner_id: string;
  owner_nom: string;
  owner_prenom: string;
  shop_name: string;
  shop_slug: string;
  shop_cover_image_url: string;
  country: string | null;
  city: string | null;
  is_verified: boolean;
  is_premium: boolean;
  category_name: string;
  category_slug: string;
  category_icon_name: string | null;
  product_name: string;
  description_short: string | null;
  brand: string | null;
  condition: 'new' | 'used' | 'refurbished';
  cover_image_url: string;
  image_2_url: string | null;
  image_3_url: string | null;
  price: number;
  promo_price: number | null;
  currency_code: string;
  stock_quantity: number;
  is_featured: boolean;
  show_whatsapp_business_button: boolean;
  whatsapp_business_phone_override: string | null;
  show_platform_chat_button: boolean;
  show_buy_button: boolean;
  buy_url: string | null;
  show_learn_more_button: boolean;
  learn_more_url: string | null;
  show_call_button: boolean;
  call_phone: string | null;
  published_at: string;
};

const PAGE_SIZE = 20;

export function useMarketFeed() {
  const [products, setProducts] = useState<MarketFeedProduct[]>([]);
  const [loading, setLoading]         = useState(true);       // chargement initial
  const [loadingMore, setLoadingMore] = useState(false);      // pagination
  const [refreshing, setRefreshing]   = useState(false);      // pull-to-refresh
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef  = useRef(0);
  const loadingRef = useRef(false); // anti double-appel concurrent

  const fetchFeed = useCallback(async (mode: 'initial' | 'more' | 'refresh') => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    if (mode === 'refresh') setRefreshing(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-feed',
          access_token: token,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger le marché');
        return;
      }

      const newProducts: MarketFeedProduct[] = data.products || [];

      if (mode === 'more') {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.product_id));
          return [...prev, ...newProducts.filter(p => !existingIds.has(p.product_id))];
        });
      } else {
        setProducts(newProducts);
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newProducts.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useMarketFeed]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchFeed('initial');
  }, [fetchFeed]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchFeed('more');
  }, [hasMore, fetchFeed]);

  const refresh = useCallback(() => {
    fetchFeed('refresh');
  }, [fetchFeed]);

  return { products, loading, loadingMore, refreshing, error, hasMore, loadMore, refresh };
}
